import type {
    Currency,
    IInvoiceHeaderBlockResponse,
    IPageResponse,
    IPaymentScheduleBlockResponse,
    IServicesBlockResponse
} from '~/types/server.types';
import type { IInvoiceBlockResponse } from '~/types/server.types';
import type { InvoiceColumnHeaderBlock } from '~/models/blocks/invoice-column-header-block.model';
import type { InvoiceSummaryBlock } from '~/models/blocks/invoice-summary-block.model';
import {
    GeneralOutboundEvents,
    CoreOutboundEvents,
    GeneralInboundEvents,
    CoreInboundEvents,
    InvoiceInboundEvents,
    ContractInboundEvents,
    NavigationEvents,
    ContractOutboundEvents,
    PageInboundEvents,
    PageOutboundEvents,
    InvoiceOutboundEvents,
    ReusableInboundEvents,
    SchedulerInboundEvents,
    SchedulerOutboundEvents
} from '~/components/Builder/Mobile/builderMobileApi.enums';

import type {
    AmountCalcType,
    DateCalcType,
    PaymentStatus,
    SmartDatePeriodRelation,
    SmartDatePeriodType
} from '~/types/model.types';
import { TBlockDataForServer } from '~/types/builder.types';
import {
    SignatureBlock,
    SignatureBlockDataForServer
} from '~/models/blocks/signature-block.model';
import { ChargeBlock } from '~/models/blocks/charge-block.model';
import { Flow } from '~/models/flow.model';

export type IMobilePage = Partial<IPageResponse> & {
    isInvoiceContainerBlock: boolean;
    isPaymentPage: boolean;
    // isPayContainerBlock might be redundant and should be removed from mobile as well
    isPayContainerBlock: boolean;
    isContractContainerBlock: boolean;
    isServiceSelectionContainerBlock: boolean;
    isContractRecaps: boolean;
    isValid: boolean;
};

export interface MobileInvoiceBlockData extends IInvoiceBlockResponse {
    isBlockOverlayOn?: boolean;
    isRecurring?: boolean;
    isDeleteDisabled?: boolean;
    isRecurringUnderMinimumDays?: boolean;
    chargeBlock?: Partial<ChargeBlock>;
    invoice_summary_block?: Partial<InvoiceSummaryBlock>;
    invoice_column_header_block?: Partial<InvoiceColumnHeaderBlock>;
    chargeContainerBlockId?: string;
}

export type InvoiceAppearanceExtendedServices = IServicesBlockResponse & {
    invoice_column_header_block?: Partial<InvoiceColumnHeaderBlock>;
    invoice_summary_block?: Partial<InvoiceSummaryBlock>;
    chargeBlock?: Partial<ChargeBlock>;
};

export const InvoiceMobileBaseProps = ['block_id', 'page_id', 'type', 'styles'];

export const InvoiceSummaryMobileProps = [
    ...InvoiceMobileBaseProps,
    'invoice_summary'
];

export enum BuilderAPIHandlerTypes {
    ContractHandler = 'ContractHandler',
    InvoiceHandler = 'InvoiceHandler',
    ScheduleHandler = 'ScheduleHandler',
    ReusableHandler = 'ReusableHandler',
    PageHandler = 'PageHandler'
}

export type PaymentScheduleMobileEntity = IPaymentScheduleBlockResponse & {
    chargeBlockId: string;
    isPublished: boolean;
    currency: Currency;
    isPaymentScheduleBlockDisabled: boolean;
    grandTotal: number;
    errorMessage: string;
    totalNumberOfPayments: number;
    paymentBlocks: PaymentBlockMobileEntity[];
    invoice_summary_block?: Partial<InvoiceSummaryBlock>;
};

export type PaymentBlockMobileEntity = {
    blockId: string;
    paymentId: string;
    isDeletable: boolean;
    paymentStatus: PaymentStatus;
    amount: number;
    amountCalcType: AmountCalcType;
    remainedMaxCustom: number;
    dateCalcType: DateCalcType;
    percentage: number;
    dueDate: string;
    isEditable: boolean;
    smartDatePeriod: number;
    smartDatePeriodType: SmartDatePeriodType;
    smartDatePeriodRelation: SmartDatePeriodRelation;
    isMilestone: boolean;
    bottomText: string;
    isAutoPay: boolean;
    autoPay: MobileAutoPayData;
    milestoneDetails: { completedDate?: string; remindersList?: string[] };
    shouldShowMarkAsPaid?: boolean;
    shouldShowViewReceipt?: boolean;
    shouldShowUndoMarkAsPaid?: boolean;
    shouldShowMarkAsRefunded?: boolean;
    shouldShowSendReminder?: boolean;
};

export type MobileAutoPayData = {
    title: string;
    description: string;
};

export type AllOutboundEvents =
    | (typeof GeneralOutboundEvents)[keyof typeof GeneralOutboundEvents]
    | (typeof ContractOutboundEvents)[keyof typeof ContractOutboundEvents]
    | (typeof InvoiceOutboundEvents)[keyof typeof InvoiceOutboundEvents]
    | (typeof SchedulerOutboundEvents)[keyof typeof SchedulerOutboundEvents]
    | (typeof PageOutboundEvents)[keyof typeof PageOutboundEvents]
    | (typeof NavigationEvents)[keyof typeof NavigationEvents]
    | (typeof CoreOutboundEvents)[keyof typeof CoreOutboundEvents];

export type AllInboundEvents =
    | GeneralInboundEvents
    | CoreInboundEvents
    | InvoiceInboundEvents
    | ContractInboundEvents
    | SchedulerInboundEvents
    | PageInboundEvents
    | ReusableInboundEvents
    | NavigationEvents;

export type TNativeAction = {
    eventName: string;
    eventData?: Record<string, unknown>;
};

export interface IMobileAppService {
    goToReports?: () => void;
    goToPayments?: () => void;
    goToBankDetails?: () => void;
    goToProfitAndLoss?: () => void;
    goToSubscriptionPage?: () => void;
    goToYearInReviewPage?: () => void;
    goToWorkspaceFile?: (
        workspaceFileId: string,
        params?: { version?: string; preview?: string | null }
    ) => void;
    switchCompany?: (companyId: string) => void;
    nativeEvent?: (action: string) => void;
    viewLoaded?: () => void;
    healthCheck?: () => void;
    triggerAppEvent?: (eventName: AllOutboundEvents, data?: any) => void;
    // todo: remove the trigger line when we go react first because it's in a PubSub hook in the React Repo
    trigger?(inboundEvent: AllInboundEvents, data: any): void;
    on?: (
        inboundEvent: AllInboundEvents,
        handler: (_) => any,
        scope: any
    ) => void;
    off?: (inboundEvent: AllInboundEvents) => void;
    offAll?: (_) => void;
    OUTBOUND?: {
        print: 'hb_app_print';
        print_pdf: 'hb_app_print_pdf';
        workspace_file_loaded: 'hb_app_workspace_file_loaded';
        edit_agreement_field: 'hb_app_edit_agreement_field';
        add_signature: 'hb_app_add_signature';
        sign_opened: 'hb_app_sign_opened';
        file_updated: 'hb_app_file_updated';
        validate_agreement_result: 'hb_app_validate_agreement_result';
        file_step_active: 'hb_app_file_step_active';
        open_file: 'hb_app_open_file';
        open_flow: 'hb_app_open_flow';
        native_controller_loaded: 'hb_app_native_controller_loaded';
        view_loaded: 'hb_app_view_loaded';
        native_controller_health_check: 'hb_app_native_controller_health_check';

        FLOW: {
            FLOW_LOADED: 'hb_app_flow_loaded';
        };
    };
}

export type UpdateBlockDataPayload = Partial<
    TBlockDataForServer & {
        canDeleteSignature?: boolean;
        usedSigneeIds?: string[];
    } & Pick<Flow, 'members'>
>;

export type MobileInvoiceHeaderBlockEventData = Partial<
    TBlockDataForServer & {
        billToUser: string;
        isDisabledDropdown: boolean;
    }
>;

export type BlockSelectedDataPayload = Partial<
    TBlockDataForServer & {
        isBlockOverlayOn?: boolean;
        flowContainsInvoice?: boolean;
        isContractDisabled?: boolean;
        isDeleteDisabled?: boolean;
        isReuseInvoiceAvailable?: boolean;
        isOverlayShownForInvoice?: boolean;
        isRecurring?: boolean;
        billToUser?: string;
        assignedSignaturesMembers: SignatureBlockDataForServer[];
        isDisabledDropdown?: boolean;
        invoice_column_header_block?: Partial<InvoiceColumnHeaderBlock>;
        invoice_summary_block?: Partial<InvoiceSummaryBlock>;
        isPaymentsRecapDisabled?: boolean;
        isServicesRecapDisabled?: boolean;
        isPaymentScheduleUpdate?: boolean;
        isPaymentScheduleBlockDisabled?: boolean;
        chargeBlock: Partial<ChargeBlock> & {
            isDeleteDisabled?: boolean;
            isRecurring?: boolean;
            isRecurringUnderMinimumDays?: boolean;
        };
        isContractContentValid?: any;
        canDeleteSignature?: boolean;
        usedSigneeIds?: string[];
        isFutureMeeting?: boolean;
        isMultiStageFinished?: boolean;
        isAllPaymentsPaid?: boolean;
    } & Pick<Flow, 'members'> &
        Pick<IInvoiceHeaderBlockResponse, 'invoice_header'> &
        Pick<SignatureBlock, 'signature'>
>;
